import { Fragment, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/primitives';
import { DocLayout, DocPage, DocBlock, DocRow } from '../../components/document';
import { printToPdf } from '../../lib/printToPdf';
import { useKontrolniDen, useKdAttachments, useKdPhotos } from '../../api/hooks';
import { mergeWithDefaults, czDate, KdPhotoThumb } from './KontrolniDenEditor';

/**
 * SdilenyKdView — read-only náhled kontrolního dne, který byl přihlášenému
 * uživateli nasdílen. Žádná editace, žádné akce kromě tisku.
 * Route: /app/kontrolni-dny/nasdilene/:stavbaId/:kdId
 */
export function SdilenyKdView() {
  const navigate = useNavigate();
  const { stavbaId: sidParam, kdId: kdParam } = useParams<{ stavbaId: string; kdId: string }>();
  const stavbaId = sidParam ? Number(sidParam) : null;
  const kdId = kdParam ? Number(kdParam) : null;

  const { data: kd, isLoading, error } = useKontrolniDen(stavbaId, kdId);
  const { data: attachments = [] } = useKdAttachments(stavbaId, kdId);
  const { data: kdPhotos = [] } = useKdPhotos(stavbaId, kdId);
  const previewRef = useRef<HTMLDivElement | null>(null);

  const data = useMemo(() => (kd ? mergeWithDefaults(kd.data_json) : null), [kd]);
  const cisloLabel = kd ? `KD-${String(kd.cislo).padStart(2, '0')}` : '—';
  // Řádky sekce 01 skryté/přidané v editoru (data_json.radky).
  const skryte = useMemo(() => new Set(data?.radky?.hidden ?? []), [data]);
  const vlastniS01 = data?.radky?.custom?.s01 ?? [];

  if (isLoading) {
    return (
      <DocLayout breadcrumb={['Realizace', 'Nasdílené KD']} title="Načítám…" statusText="Načítám" status="draft">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám…</div>
      </DocLayout>
    );
  }

  if (error || !kd || !data) {
    return (
      <DocLayout breadcrumb={['Realizace', 'Nasdílené KD']} title="Chyba" statusText="Chyba" status="draft">
        <div style={{ padding: 24, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', borderRadius: 4 }}>
          KD se nepodařilo načíst (nebo k němu nemáte přístup):{' '}
          {error instanceof Error ? error.message : 'neznámá chyba'}
        </div>
      </DocLayout>
    );
  }

  const companyName = data.firma || data.stavba || '—';

  return (
    <DocLayout
      breadcrumb={['Realizace', 'Nasdílené KD', cisloLabel]}
      title={`Zápis z kontrolního dne — ${cisloLabel}`}
      status={kd.stav === 'uzavreno' ? 'done' : 'draft'}
      statusText={kd.stav === 'uzavreno' ? 'Uzavřen' : 'Jen ke čtení'}
      actions={
        <>
          <Button variant="secondary" onClick={() => navigate('/app/kontrolni-dny/nasdilene')}>← Nasdílené</Button>
          <Button variant="secondary" onClick={() => printToPdf('Kontrolni_den.pdf')} leading="⎙">Tisk</Button>
        </>
      }
    >
      <div
        className="docNoPrint"
        style={{ marginBottom: 14, background: '#eef2f7', border: '1px solid #cbd5e1', borderRadius: 4, padding: '8px 14px', fontSize: 12.5, color: '#475569' }}
      >
        👁 Tento kontrolní den vám byl nasdílen <strong>jen pro čtení</strong>.
      </div>

      <div ref={previewRef}>
        <DocPage
          company={{
            initials: companyName.split(/\s+/).filter(Boolean).slice(0, 3).map((w) => w[0]?.toUpperCase() ?? '').join('') || '—',
            name: companyName,
            address: data.stavba || '',
          }}
          tag="Kontrolní den"
          title="Zápis z kontrolního dne"
          meta={`č. ${cisloLabel} · ${data.datum || czDate(kd.datum) || '—'}`}
          footer={{ left: companyName, center: 'str. 1 / 1', right: cisloLabel }}
        >
          {/* 01 — Základní identifikace */}
          <DocBlock idx="01" title="Základní identifikace">
            {!skryte.has('s01.cislo') && <DocRow k="Číslo zápisu" mono v={cisloLabel} />}
            {!skryte.has('s01.datum') && <DocRow k="Datum" mono v={data.datum || czDate(kd.datum) || '—'} />}
            {!skryte.has('s01.smlouva') && <DocRow k="Smlouva o dílo" v={data.smlouva || '—'} />}
            {!skryte.has('s01.misto') && <DocRow k="Místo konání" v={data.misto || '—'} />}
            {!skryte.has('s01.cas') && <DocRow k="Čas zahájení" mono v={data.casZahajeni || '—'} />}
            {!skryte.has('s01.termin') && <DocRow k="Termín dokončení díla" mono v={data.terminDokonceni || '—'} />}
            {vlastniS01.map((r) => (
              <DocRow key={r.id} k={r.k || '—'} v={r.v || '—'} />
            ))}
          </DocBlock>

          {/* 02 — Účastníci */}
          <DocBlock idx="02" title="Účastníci">
            {data.ucast.map((g) => (
              <Fragment key={g.id}>
                <DocRow k={g.nazev} v={g.subjekt || '—'} />
                {g.ico && <DocRow k="IČO" mono v={g.ico} />}
                {g.adresa && <DocRow k="Adresa" v={g.adresa} />}
                {g.ucastnici.map((p, i) => (
                  <Fragment key={p.id}>
                    <DocRow
                      k={`${g.nazev} — ${i + 1}. jméno`}
                      v={
                        <span>
                          {p.jmeno || '—'}
                          {p.pritomen && <span style={{ color: 'var(--sd-success)', marginLeft: 8, fontSize: 11, fontWeight: 700 }}>✓ přítomen</span>}
                        </span>
                      }
                    />
                    {p.telefon && <DocRow k="Telefon" mono v={p.telefon} />}
                    {p.email && <DocRow k="E-mail" mono v={p.email} />}
                    {p.podpis && (
                      <DocRow
                        k="Podpis"
                        v={<img src={p.podpis} alt="podpis" style={{ maxWidth: 280, maxHeight: 90, objectFit: 'contain' }} />}
                      />
                    )}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </DocBlock>

          {/* 03 — Stav prací */}
          <DocBlock idx="03" title="Stav prací na stavbě">
            <DocRow k="Popis" v={data.stavPraci || '—'} />
          </DocBlock>

          {/* 04 — Harmonogram */}
          <DocBlock idx="04" title="Kontrola termínů a harmonogramu">
            {data.harmonogram.length === 0 && (
              <DocRow k="—" v={<span style={{ color: 'var(--sd-doc-dim)' }}>Žádný záznam.</span>} />
            )}
            {data.harmonogram.map((r) => (
              <Fragment key={r.id}>
                <DocRow k="Položka / etapa" v={r.polozka || '—'} />
                <DocRow k="Plánovaný termín" mono v={r.plan || '—'} />
                <DocRow k="Skutečný stav" v={r.skutecnost || '—'} />
              </Fragment>
            ))}
          </DocBlock>

          {/* 05 — Program */}
          <DocBlock idx="05" title="Body programu / projednávané věci">
            {data.program.length === 0 && (
              <DocRow k="—" v={<span style={{ color: 'var(--sd-doc-dim)' }}>Žádné body.</span>} />
            )}
            {data.program.map((r, i) => (
              <Fragment key={r.id}>
                <DocRow k={`${i + 1}. Popis`} v={r.popis || '—'} />
                <DocRow k="Odpovědný" v={r.odpovedny || '—'} />
                <DocRow k="Termín" mono v={r.termin || '—'} />
              </Fragment>
            ))}
          </DocBlock>

          {/* 06 — Vady */}
          <DocBlock idx="06" title="Vady a nedodělky">
            {data.vady.length === 0 && (
              <DocRow k="—" v={<span style={{ color: 'var(--sd-doc-dim)' }}>Žádné vady evidované v tomto zápise.</span>} />
            )}
            {data.vady.map((r, i) => (
              <Fragment key={r.id}>
                <DocRow k={`Vada ${i + 1}`} v={r.popis || '—'} />
                <DocRow k="Místo" v={r.misto || '—'} />
                <DocRow k="Termín odstranění" mono v={r.termin || '—'} />
              </Fragment>
            ))}
          </DocBlock>

          {/* 07 — Přílohy */}
          <DocBlock idx="07" title="Přílohy">
            <DocRow k="Seznam" v={data.prilohy || '—'} />
          </DocBlock>

          {/* 08 — Rozeslání */}
          <DocBlock idx="08" title="Rozeslání">
            <DocRow k="Příjemci" v={data.rozeslani || '—'} />
          </DocBlock>
        </DocPage>
      </div>

      {/* Fotky + přílohy — read-only, netiskne se */}
      <div className="docNoPrint" style={{ marginTop: 20, display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))' }}>
        <div style={{ background: '#fff', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--sd-yellow)' }} />
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sd-doc-ink)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4, marginBottom: 8 }}>
            📷 Fotky z KD ({kdPhotos.length})
          </div>
          {kdPhotos.length === 0 ? (
            <div style={{ color: 'var(--sd-doc-dim)', fontSize: 12, padding: '10px 0' }}>Žádné fotky.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {stavbaId != null && kdPhotos.map((p) => (
                <KdPhotoThumb key={p.id} stavbaId={stavbaId} photoId={p.id} label={p.popis || p.filename} />
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#fff', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--sd-yellow)' }} />
          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sd-doc-ink)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4, marginBottom: 8 }}>
            📄 Přílohy / skeny ({attachments.length})
          </div>
          {attachments.length === 0 ? (
            <div style={{ color: 'var(--sd-doc-dim)', fontSize: 12, padding: '10px 0' }}>Žádné přílohy.</div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {attachments.map((a) => (
                <li
                  key={a.id}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--sd-w-faf8ef, #faf8ef)', border: '1px solid var(--sd-w-ece8d8, #ece8d8)', borderRadius: 4 }}
                >
                  <a
                    href={`/api/stavby/${stavbaId}/kd/${kdId}/attachments/${a.id}/content`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--sd-doc-ink)', fontSize: 12, fontWeight: 600, textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={a.filename}
                  >
                    {a.filename}
                  </a>
                  <span style={{ fontSize: 11, color: 'var(--sd-doc-dim)', fontFamily: 'var(--sd-font-mono)' }}>
                    {(a.size / 1024).toFixed(1)} kB
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DocLayout>
  );
}
